<template>
    <div v-if="!tracking">
        <template v-if="items.length">
            <slot name="title"></slot>
            <template v-if="!hideList">
                <div v-if="errorLoading" class="text-center p-5 text-danger">
                    <div class="p-2">
                        <i class="fa fa-exclamation-triangle fa-4x" />
                    </div>
                    <div>
                        <strong>Error loading recent patients</strong>
                    </div>
                </div>
                <component :is="menuItems ? 'ul' : 'b-list-group'" b-list-group v-else>
                    <template v-for="(i, index) in items">
                        <component :is="menuItems ? 'li' : 'b-list-group'" v-if="loading" :key="i.id" disabled>
                            <b-skeleton />
                        </component>
                        <component :is="menuItems ? 'li' : 'b-list-group-item'" v-else :key="i.id"
                            :class="{ 'selected': isSelected(index) }"
                            @mouseenter="$emit('hover', index)"
                            @click="!menuItems && (!disablePatient || disablePatient?.id !== i.id) ? $emit('change', i) : null"
                            :active="selected?.id === i.id || (keyboardNav && itemIndex === index)"
                            :disabled="disablePatient?.id === i.id">
                            <RouterLink v-if="menuItems" :to="`/processing/patient-dashboard/${i.id}`"
                                @click.native.exact="$emit('change', i)" v-html="i.toString()" />
                            <span v-else v-html="i.toString()" />
                        </component>
                    </template>
                </component>
            </template>
        </template>
        <div v-else-if="noItemsMessage" class="text-center p-5 text-muted">
            <div class="p-2">
                <i class="fa fa-user fa-4x" />
            </div>
            No recent patients
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Patient } from "@/models/Patient";
import { AxiosRequestConfig } from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { getUserSettingParsed, saveUserSetting } from "@/services/UserSettingsService";

const recentStorageKey = "recent-patients";

@Component({
    name: "PatientRecentlyViewed",
    components: {}
})
export default class PatientRecentlyViewed extends Vue {

    @Prop({ required: false, default: false }) private tracking!: boolean;
    @Prop({ required: false }) private patient?: Patient | null;
    @Prop({ required: false }) private selected?: Patient | null;
    @Prop({ required: false, default: false }) private hideList!: boolean;
    @Prop({ required: false }) private disablePatient?: Patient | null;
    @Prop({ required: false, default: false }) private keyboardNav!: boolean;
    @Prop({ required: false, default: false }) private noItemsMessage!: boolean;
    @Prop({ default: false }) private menuItems!: boolean;
    @Prop({ default: -1 }) private selectedIndex!: number;

    public items: Array<Patient> = [];
    private itemIndex = 0;
    protected loading = false;
    protected errorLoading = false

    mounted() {
        if (this.tracking) return;

        this.getItems();
        if (this.keyboardNav) {
            document.addEventListener("keyup", this.hotKeyListener);
        }
    }

    isSelected(index: number): boolean {
        return this.selectedIndex === index;
    }

    beforeDestroy() {
        document.removeEventListener("keyup", this.hotKeyListener);
    }

    hotKeyListener(e: any) {
        if (this.loading) return;
        if (e.code === "ArrowDown") {
            if (this.itemIndex < this.items.length - 1) this.itemIndex += 1;
        } else if (e.code === "ArrowUp") {
            if (this.itemIndex > 0) this.itemIndex -= 1;
        } else if (e.code === "Enter" && this.items.length) {
            this.$emit('change', this.items[this.itemIndex]);
        }
    }

    async getItems() {
        const recents = this.getRecentsFromHistory();

        if (!recents.length) return;

        try {
            this.errorLoading = false;
            this.loading = true;

            const config: AxiosRequestConfig = {
                params: {
                    searchTerm: `[[${recents.join(",")}]]`
                }
            }

            this.items = recents.map(r => Object.assign(new Patient(), { id: r }));

            const response = await this.$http.get(`/patient`, config);

            this.items = response.data.map((i: any) => Object.assign(new Patient(), i));
            this.$emit('item-count', this.items.length);

            if (this.$route.name === "PatientDashboard") {
                this.itemIndex = this.items.findIndex(i => i.id.toString() === this.$route.params.id);
                if (this.itemIndex === -1) this.itemIndex = 0;
            }
        } catch (err) {
            this.errorLoading = true;
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    getRecentsFromHistory(): Array<number> {
        return getUserSettingParsed<Array<number>>(recentStorageKey, []);
    }

    @Watch("patient")
    trackPatientView() {
        if (!this.tracking || !this.patient?.id) return;

        const recents = this.getRecentsFromHistory();

        const id = parseInt(this.patient.id.toString());

        if (isNaN(id)) return;

        const index = recents.indexOf(id);

        if (index !== -1) {
            recents.splice(index, 1);
        }

        recents.unshift(id);

        if (recents.length > 10) {
            recents.pop();
        }

        saveUserSetting(recentStorageKey, JSON.stringify(recents));
    }

}

</script>

<style scoped>
li.selected {
    background: rgb(0, 162, 255) !important;
    color: white !important;
}

li a {
    color: black;
    text-decoration: none;
    width: 100%;
    display: block;
}

li:hover a,
li.selected a {
    color: white !important;
}

/* Bootstrap Vue list group item hover styles (when menuItems=false) */
::v-deep(.list-group-item) {
    cursor: pointer;
}

::v-deep(.list-group-item:hover:not(.disabled)) {
    background-color: #f8f9fa;
}

::v-deep(.list-group-item.active) {
    background-color: rgb(0, 162, 255) !important;
    border-color: rgb(0, 162, 255) !important;
    color: white !important;
}

/* Remove extra spacing at the bottom when used in menu context */
ul {
    margin-bottom: 0 !important;
}
</style>
